import { computed, ref } from 'vue'

import { defineStore } from 'pinia'
import { userService } from '@/database/services'

export const useUserStore = defineStore('user', () => {
  // State
  const users = ref([])
  const loading = ref(false)
  const error = ref(null)

  // Getters
  const getUsers = computed(() => users.value)
  const getLoading = computed(() => loading.value)
  const getError = computed(() => error.value)

  // Actions
  const fetchUsers = async (filters = {}) => {
    try {
      loading.value = true
      error.value = null
      const data = await userService.getAll(filters)
      users.value = data
      return data
    } catch (err) {
      error.value = err.message
      console.error('Error fetching users:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const fetchUserById = async (id) => {
    try {
      loading.value = true
      error.value = null
      const data = await userService.getById(id)
      return data
    } catch (err) {
      error.value = err.message
      console.error('Error fetching user:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const createUser = async (userData) => {
    try {
      loading.value = true
      error.value = null
      const data = await userService.create(userData)
      users.value.push(data)
      return data
    } catch (err) {
      error.value = err.message
      console.error('Error creating user:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const updateUser = async (id, userData) => {
    try {
      loading.value = true
      error.value = null
      const data = await userService.update(id, userData)
      const index = users.value.findIndex(u => u.id === id)
      if (index !== -1) {
        users.value[index] = data
      }
      return data
    } catch (err) {
      error.value = err.message
      console.error('Error updating user:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const deleteUser = async (id) => {
    try {
      loading.value = true
      error.value = null
      await userService.delete(id)
      users.value = users.value.filter(u => u.id !== id)
      return true
    } catch (err) {
      error.value = err.message
      console.error('Error deleting user:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const fetchUsersByRole = async (role) => {
    try {
      loading.value = true
      error.value = null
      const data = await userService.getByRole(role)
      return data
    } catch (err) {
      error.value = err.message
      console.error('Error fetching users by role:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const fetchUsersByTeam = async (teamId) => {
    try {
      loading.value = true
      error.value = null
      const data = await userService.getByTeam(teamId)
      return data
    } catch (err) {
      error.value = err.message
      console.error('Error fetching users by team:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const searchUsers = async (searchTerm) => {
    try {
      loading.value = true
      error.value = null
      const data = await userService.search(searchTerm)
      return data
    } catch (err) {
      error.value = err.message
      console.error('Error searching users:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const clearError = () => {
    error.value = null
  }

  const clearUsers = () => {
    users.value = []
  }

  // Convenience methods that match what views expect
  const getUsersFunction = async (filters = {}) => {
    return await fetchUsers(filters)
  }

  const getUser = async (id) => {
    return await fetchUserById(id)
  }

  const getUserByEmail = async (email) => {
    return await userService.getByEmail(email)
  }

  return {
    // State
    users,
    loading,
    error,
    // Getters (computed)
    usersList: getUsers, // computed getter renamed to avoid conflict
    getLoading,
    getError,
    // Actions
    fetchUsers,
    fetchUserById,
    createUser,
    updateUser,
    deleteUser,
    fetchUsersByRole,
    fetchUsersByTeam,
    searchUsers,
    clearError,
    clearUsers,
    // Convenience methods - function versions for backwards compatibility
    getUsers: getUsersFunction, // Function version for components expecting it
    getUserById: getUser,
    getUserByEmail: getUserByEmail
  }
})
